'use client';

/**
 * An audible ring for the front desk, synthesised rather than loaded.
 *
 * WHY THIS EXISTS. Push notifications (R4) fire when the staff tab is
 * BACKGROUNDED. A staff member with the app open and the tab focused — the
 * normal state at a front desk — got no sound at all: the incoming-call panel
 * appeared silently, and if they were looking at a reservation on the same
 * screen they simply missed it. "The app was open, why didn't I hear it?" is
 * the failure this closes.
 *
 * SYNTHESISED, not an audio file. An asset would need bundling, a network
 * fetch that can fail exactly when the call arrives, and a licence. Two
 * oscillators through a gain envelope reproduce the standard double-ring in a
 * few lines and cannot 404.
 *
 * NO `setInterval`/`setTimeout` — the project bans both. The repeat is driven
 * by the Web Audio clock: every tone is scheduled ahead on `ctx.currentTime`,
 * which is sample-accurate and unaffected by a throttled timer in a background
 * tab. Scheduling is the mechanism, not a workaround.
 */

/** Standard UK-style double ring: two 400ms bursts, then a gap. */
const RING_PATTERN = [
  { start: 0.0, duration: 0.4 },
  { start: 0.6, duration: 0.4 },
];
const CYCLE_SECONDS = 3;
/** How many cycles to schedule at once. The ring deadline is 60s. */
const CYCLES = 20;

let ctx: AudioContext | null = null;
let stopAt: number | null = null;
let nodes: AudioNode[] = [];

function audioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx || ctx.state === 'closed') ctx = new Ctor();
  return ctx;
}

/**
 * Start ringing. Safe to call repeatedly — a second call is a no-op while a
 * ring is already scheduled, so a re-render cannot stack overlapping tones.
 */
export function startRingtone(): void {
  const audio = audioContext();
  if (!audio) return;

  // Only refuse while a ring is still SCHEDULED to play.
  //
  // `stopAt` is the clock time the last tone ends. Testing it for non-null
  // alone was a latent bug of mine: a ring left to run its full 60 seconds
  // never passes through `stopRingtone`, so `stopAt` stayed set and the NEXT
  // incoming call was refused here — silently, and only after a call had gone
  // unanswered, which is exactly when the desk least wants a silent ring.
  //
  // Comparing against the clock makes an expired schedule indistinguishable
  // from no schedule, which is what it actually is.
  if (stopAt !== null && audio.currentTime < stopAt) return;
  if (stopAt !== null) stopRingtone();

  // Browsers suspend an AudioContext created without a user gesture. Resuming
  // is best-effort: if the staff member has not yet interacted with the page
  // there is nothing we can lawfully play, and the visual panel still shows.
  if (audio.state === 'suspended') void audio.resume().catch(() => undefined);

  const master = audio.createGain();
  master.gain.value = 0.0001;
  master.connect(audio.destination);
  nodes.push(master);

  const begin = audio.currentTime + 0.05;

  for (let cycle = 0; cycle < CYCLES; cycle++) {
    for (const burst of RING_PATTERN) {
      const at = begin + cycle * CYCLE_SECONDS + burst.start;

      // Two tones, as a real ring is — a single sine reads as an alarm.
      for (const frequency of [440, 480]) {
        const osc = audio.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = frequency;

        // Per-burst envelope. Ramping rather than switching avoids the click
        // a hard gate produces at these levels.
        const gain = audio.createGain();
        gain.gain.setValueAtTime(0.0001, at);
        gain.gain.exponentialRampToValueAtTime(0.18, at + 0.02);
        gain.gain.setValueAtTime(0.18, at + burst.duration - 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, at + burst.duration);

        osc.connect(gain);
        gain.connect(master);
        osc.start(at);
        osc.stop(at + burst.duration + 0.01);
        nodes.push(osc, gain);
      }
    }
  }

  master.gain.setValueAtTime(1, begin);
  stopAt = begin + CYCLES * CYCLE_SECONDS;
}

/**
 * Stop immediately — the call was answered, declined, or taken by a colleague.
 *
 * Every scheduled node is disconnected rather than left to finish: tones are
 * scheduled up to a minute ahead, so without this a ringtone would keep playing
 * over a live conversation.
 */
export function stopRingtone(): void {
  if (stopAt === null) return;
  stopAt = null;

  for (const node of nodes) {
    try {
      if ('stop' in node && typeof (node as OscillatorNode).stop === 'function') {
        (node as OscillatorNode).stop();
      }
      node.disconnect();
    } catch {
      // Already stopped or disconnected — both are the state we want.
    }
  }
  nodes = [];
}

/** True while a ring is scheduled. Exported for tests. */
export function isRinging(): boolean {
  return stopAt !== null;
}
